import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Box,
  Typography,
  Tooltip,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useTranslation } from './LocalizationProvider';
import { formatJsonToPlainText } from '../util/formatter';
import JsonPrettyPrinter from './JsonPrettyPrinter';
import { handleErrorResponse } from '../util/utils';

// The report is wrapped in an envelope: { status, analysis }. 'expanded' means
// the backend fell back to the 90-day lookback because the last 30 days were
// too sparse, and 'insufficient' means there is nothing to render at all.
const parseEnvelope = (raw) => {
  const parsed = typeof raw === 'string'
    ? (raw.trim() ? JSON.parse(raw) : null)
    : raw;
  if (!parsed) {
    return { status: 'ok', analysis: [] };
  }
  // Tolerate the pre-envelope shape, which was a bare array of locations.
  if (Array.isArray(parsed)) {
    return { status: 'ok', analysis: parsed };
  }
  return {
    status: parsed.status || 'ok',
    analysis: Array.isArray(parsed.analysis) ? parsed.analysis : [],
  };
};

const AIAnalysisDialog = ({ deviceId, open, handleClose }) => {
  const t = useTranslation();
  const [result, setResult] = useState({ status: 'ok', analysis: [] });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const device = useSelector((state) => state.devices.items[deviceId]);

  useEffect(() => {
    async function fetchData() {
      // The lookback windows are fixed at 30 and 90 days server-side, so these
      // values are ignored by the analysis. They still have to be sent: the
      // endpoint logs the range before it gets there and formats the dates
      // unguarded, so omitting them NPEs into a 400. Send the widest real
      // window rather than device fields, which can be missing.
      const toDate = new Date();
      const fromDate = new Date(toDate.getTime() - 90 * 24 * 60 * 60 * 1000);

      const query = new URLSearchParams({
        deviceId,
        from: fromDate.toISOString(),
        to: toDate.toISOString(),
      }).toString();

      try {
        const response = await fetch(`/api/reports/topstopanalysis?${query}`, {
          headers: { Accept: 'application/json' },
        });
        if (response.ok) {
          const jsonData = await response.json();
          try {
            setResult(parseEnvelope(jsonData.data));
          } catch (parseError) {
            setResult({ status: 'ok', analysis: [] });
          }
        } else {
          await handleErrorResponse(response);
        }
      } catch (fetchError) {
        // Handle error gracefully by showing the error message
        setError(fetchError.message || t('sharedError'));
      } finally {
        setLoading(false);
      }
    }

    if (open) {
      fetchData();
    }
  }, [deviceId, open]);

  const { status, analysis } = result;
  const insufficient = status === 'insufficient';
  const expanded = status === 'expanded';

  const handleCopyToClipboard = () => {
    const body = analysis.map((item) => formatJsonToPlainText(item)).join('\n\n');
    // Keep the caveat attached to the numbers once they leave the app.
    const plainText = expanded ? `${t('reportAIAnalysisExpanded')}\n\n${body}` : body;
    navigator.clipboard.writeText(plainText);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          width: '60%',
          maxWidth: '80%',
          height: '70%',
        },
      }}
    >
      <DialogTitle>
        {`${t('reportAIAnalysis')}: ${device.name}`}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          style={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent style={{ display: 'flex', flexDirection: 'column', padding: '16px', height: '100%' }}>
        <Box style={{ flex: 1, overflowY: 'auto', paddingRight: '16px' }}>
          {loading ? (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100%">
              <CircularProgress />
              <Typography style={{ marginTop: '16px' }}>{t('reportAIAnalyzingData')}</Typography>
            </Box>
          ) : (
            error ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <Typography color="error" align="center">{error}</Typography>
              </Box>
            ) : (
              insufficient ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <Typography align="center">{t('reportAIAnalysisInsufficient')}</Typography>
                </Box>
              ) : (
                <Box position="relative">
                  <Tooltip title={t('sharedCopyToClipboard')}>
                    <IconButton
                      onClick={handleCopyToClipboard}
                      style={{ position: 'absolute', right: 0, top: 0 }}
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                  {expanded && (
                    <Alert severity="info" style={{ marginRight: '48px' }}>
                      {t('reportAIAnalysisExpanded')}
                    </Alert>
                  )}
                  {analysis.length ? (
                    <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', padding: '16px', overflowX: 'auto', fontFamily: 'Arial, sans-serif', lineHeight: '1.5' }}>
                      <JsonPrettyPrinter data={analysis} />
                    </div>
                  ) : (
                    <Typography>{t('sharedNoData')}</Typography>
                  )}
                </Box>
              )
            )
          )}
        </Box>
        <Box style={{ paddingTop: '1em' }}>
          <Typography variant="body2" align="center">
            {t('sharedAIGeneratedDisclaimer')}
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AIAnalysisDialog;
